import { useRef, useCallback, useState, useEffect } from 'react';

import { parse } from '../syntax';
import { CustomElement, CustomEditor } from '../types';
import { findAllChipNodes, initializeEditorFromString, serializeQueryElements } from '../utils';

export const useEditorValue = (
  externalQueryString = '',
  editor: CustomEditor,
  onChange?: (value: string | undefined) => void,
) => {
  const calculateValueFromString = useCallback(() => {
    return initializeEditorFromString(editor, externalQueryString);
  }, [externalQueryString, editor]);

  const [value, setValue] = useState<CustomElement[]>(
    () => calculateValueFromString() as CustomElement[],
  );

  const valueRef = useRef(value);
  const isExternalUpdateRef = useRef(false);
  const previousExternalQueryStringRef = useRef(externalQueryString);
  const chipCountRef = useRef(0);
  const lastSentValueRef = useRef<string | undefined>(undefined);

  // Sync external query string to internal editor state
  useEffect(() => {
    if (externalQueryString !== previousExternalQueryStringRef.current) {
      // If parent is echoing back the value we just sent, don't overwrite
      if (externalQueryString === lastSentValueRef.current) {
        previousExternalQueryStringRef.current = externalQueryString;
        lastSentValueRef.current = undefined; // Clear so real external changes still work

        return;
      }

      isExternalUpdateRef.current = true;
      const newValue = calculateValueFromString() as CustomElement[];
      setValue(newValue);
      valueRef.current = newValue; // Sync ref synchronously
      previousExternalQueryStringRef.current = externalQueryString;
      lastSentValueRef.current = undefined;
    }
  }, [externalQueryString, calculateValueFromString, setValue]);

  // [PURPOSE]:
  // - If chips are added/removed, and filter is parsed as `complete` - submit the filter
  const implicitSubmit = useCallback(
    (newValue: CustomElement[]) => {
      // Skip on initial load
      if (newValue === valueRef.current) {
        return;
      }

      const newChipCount = findAllChipNodes(newValue) as number;

      if (newChipCount !== chipCountRef.current && onChange) {
        chipCountRef.current = newChipCount;
        const serializedValue = serializeQueryElements(newValue);

        try {
          const peggyParsed = parse(serializedValue);
          if (peggyParsed?.complete) {
            const valueToSend = serializedValue || undefined;
            lastSentValueRef.current = valueToSend;
            onChange(valueToSend);
          }
        } catch {
          // Ignore parsing errors, just don't submit the filter
        }
      }
    },
    [onChange, valueRef],
  );

  // [PURPOSE]:
  // - persist Slate Editor state internally
  // - trigger external onChange when chips are added or removed (in addition to the blur trigger)
  const handleChange = useCallback(
    (newValue: CustomElement[]) => {
      implicitSubmit(newValue);

      setValue(newValue);
      // Sync ref synchronously - no effect needed
      valueRef.current = newValue;

      isExternalUpdateRef.current = false;
    },
    [setValue, implicitSubmit],
  );

  return {
    handleChange,
    lastSentValueRef,
    setValue,
    value,
    valueRef,
  };
};
